/**
 * English message catalog — every user-visible string the web app
 * renders inline today. Phase F lifts these out of the page modules
 * (`MainPage.tsx`, `SettingsPage.tsx`, `PairingPage.tsx`) so a future
 * translation effort lands in ONE place.
 *
 * Grouped by feature for readability — the names mirror the headless
 * label-bag groupings in `@reflecto/client-ui` (header / settings /
 * pairing) plus `notifications` / `conversations` / `list` for the
 * three label bags `SidePanelNotificationList` consumes.
 *
 * Out of scope for this catalog (intentionally inline, not user-facing
 * surface text):
 *   - `console.warn` / log scopes (operational, not localised)
 *   - JSDoc / inline comments
 *   - log structured-event names ("theme_write_failed", etc.)
 *   - page error fallback strings that already vary per error
 *     instance (Pairing's `errorMsg` from `err.message`)
 *
 * Adding a string: drop it into the right group, then either
 * reference `messages.group.key` directly (for one-shot reads) or
 * thread it through the existing label-bag adapter in the consuming
 * page (for bags handed to headless components).
 *
 * Adding a locale: copy this file to `messages.es.ts`, translate the
 * values, and wire a runtime locale picker in `index.ts`. The
 * `Messages` type lifted in `index.ts` keeps the shapes in lock-step.
 *
 * Plurals: count-bearing functions select their form through the shared
 * `selectPlural(locale, count, forms)` helper from `@reflecto/client-core`
 * (a thin `Intl.PluralRules` wrapper) rather than a baked-in
 * `count === 1` ternary, so locales needing `few` / `many` express them
 * by supplying those keys in their own catalog.
 */
import { selectPlural } from "@reflecto/client-core";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "Connected",
    statusReconnecting: "Reconnecting…",
    statusDisconnected: "Disconnected",
    openSettings: "Open settings",
    pairedFallbackDevice: "your phone",
    /** Accessible label for the public-mode toggle when it's currently
     *  off. Tapping enables privacy mode (collapses to per-app cards). */
    publicModeToggleEnable: "Hide notification content",
    /** Accessible label for the public-mode toggle when it's currently
     *  on. Tapping disables privacy mode. */
    publicModeToggleDisable: "Show notification content",
  },

  notifications: {
    replyAriaLabel: "Reply",
    replyPlaceholder: "Reply…",
    /** ARIA label for the reply composer's send button. */
    replySend: "Send",
    sent: "Sent",
    failedToSend: "Failed to send",
    unread: "Unread",
    dismiss: "Dismiss",
    notificationImageAlt: "Notification image",
  },

  conversations: {
    replyAriaLabel: "Reply",
    replyPlaceholder: "Reply…",
    /** ARIA label for the reply composer's send button. */
    replySend: "Send",
    sent: "Sent",
    failedToSend: "Failed to send",
    dismiss: "Dismiss conversation",
    messagesListLabel: "Messages in this conversation",
    /** Sender-name token shown inline in a thread to mark the user's own
     *  messages (vs. the partner's) — a label token, not prose address. */
    deviceOwner: "You",
    // Functions stay in the catalog so plural rules live alongside
    // the strings they format via `selectPlural`. The count=0 branch
    // handles the empty case inline (a deliberate "No unread messages"
    // string, not a CLDR `zero` form); no separate "no unread" string
    // is needed because the headless component gates the call on
    // `unreadCount > 0` in the first place.
    /** aria-label for a single conversation's unread count. `count === 0`
     *  is an explicit UX branch; the noun plural is `selectPlural`-driven. */
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "No unread messages"
        : `${count} ${selectPlural("en", count, {
            one: "unread message",
            other: "unread messages",
          })}`,
  },

  list: {
    today: "Today",
    yesterday: "Yesterday",
    searchNotifications: "Search notifications",
    /** aria-label for the search box's inline clear (×) button. */
    clearSearch: "Clear search",
    clearAll: "Clear all",
    markAllRead: "Mark all read",
    /** Checkbox label — verb sense "group notifications by app", not the noun "a group". */
    groupCheckboxLabel: "Group",
    noNotificationsTitle: "No notifications yet",
    noNotificationsDesc: "New messages mirrored from your phone will appear here.",
    noMatchesTitle: "No matches",
    notificationsList: "Notifications",
    privacyAppList: "Notifications by app",
    /** Imperative verb on a per-app "dismiss all from this app" button:
     *  clears just one app's notifications — unlike `clearAll` (the whole
     *  list) and the `clearMatches` search-result clear. */
    clear: "Clear",
    // Single count-taking clear label for search results ("Clear 1 match" /
    // "Clear 5 matches"). Plural selection lives in the body via
    // `selectPlural` — the headless `SidePanelNotificationList` now calls
    // this unconditionally (no more `matchCount === 1` branch at the call
    // site), so locales like Russian/Polish/Arabic pick the right form
    // from `count` themselves.
    clearMatches: (count: number): string =>
      `Clear ${count} ${selectPlural("en", count, { one: "match", other: "matches" })}`,
    noMatchesDesc: (query: string): string => `No notifications match \u201C${query}\u201D.`,
    appNotificationsGroup: (appName: string): string => `${appName} notifications`,
    dismissAllFrom: (appName: string): string => `Dismiss all from ${appName}`,
    // `kind` is a code value (not translated) selecting the noun; the
    // singular/plural form of that noun is `selectPlural`-driven.
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("en", count, { one: "message", other: "messages" })
          : kind === "calls"
            ? selectPlural("en", count, { one: "call", other: "calls" })
            : selectPlural("en", count, { one: "notification", other: "notifications" });
      return `${count} ${noun}`;
    },
    // PRD docs/prd-keyboard.md §4.5 footer hint glyphs. Follow each
    // keycap (E and J/K). Lowercase to match the spec example
    // "E dismiss · J/K navigate".
    /** Lowercase fragment rendered after a key glyph, composing "E dismiss" —
     *  keep it a lowercase fragment that reads after a key name. */
    kbdDismiss: "dismiss",
    /** Lowercase fragment rendered after a key glyph, composing "J/K navigate" —
     *  keep it a lowercase fragment that reads after a key name. */
    kbdNavigate: "navigate",
    /** Screen-reader-only conjunction read between the J and K key names ("J or K navigate"). */
    kbdOrSeparator: "or",
  },

  settings: {
    back: "Back to notifications",
    settings: "Settings",
    appearance: "Appearance",
    theme: "Theme",
    /** Theme option meaning "automatic — follow the OS theme"; sibling of Light/Dark. */
    themeAuto: "Auto",
    themeLight: "Light",
    themeDark: "Dark",
    language: "Language",
    languageSystemDefault: "System default",
    notificationsSection: "Notifications",
    osNotifications: "OS notifications",
    callNotifications: "Call notifications",
    privacySection: "Privacy",
    analyticsToggle: "Anonymous usage statistics",
    analyticsToggleDescription: "Counts and milestones only — never your notifications.",
    connection: "Connection",
    /** Heading above the connection-status line (Connected / Reconnecting / Disconnected). */
    status: "Status",
    statusConnected: "Connected",
    statusReconnecting: "Reconnecting…",
    statusDisconnected: "Disconnected",
    disconnectFromPhone: "Disconnect from phone",
    disconnectConfirm: "Are you sure? This will unpair your device.",
    yesDisconnect: "Yes, disconnect",
    cancel: "Cancel",
    aboutAndSupport: "About & Support",
    version: "Version",
    encryption: "Encryption",
    encryptionValue: "E2E (NaCl)",
    needHelp: "Need help? Email __EMAIL__",
    // Disconnect partial-success / hard-failure banners — surface
    // copy lives outside the headless component (the `Promise<void>`
    // contract isn't rich enough to encode the partial-success
    // middle state). Functions take the underlying error message
    // suffix so we don't `+`-concat user-visible strings inline.
    disconnectFailed: "Failed to disconnect — please try again",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `Disconnected, but local cleanup failed: ${errorMsg}. Please reload the page.`,
    disconnectLocalCleanupFailed: "Disconnected, but local cleanup failed. Please reload the page.",
  },

  pairing: {
    enterCodeInstruction: "Enter this code in the Reflecto app",
    pairedSuccessfully: "Paired successfully!",
    pairingFailed: "Pairing failed",
    requestingPairingCode: "Requesting pairing code…",
    codeExpired: "Code expired",
    tryAgain: "Try Again",
    failedToStartPairing: "Failed to start pairing",
    needAppText: "Need the Android app? Email __EMAIL__ for access.",
  },

  dnd: {
    title: "Do not disturb",
    description: "Pause notifications on this device for the next hour.",
    snooze1h: "Snooze 1 hour",
    snoozeActiveUntil: (formattedTime: string): string => `Active until ${formattedTime}`,
    cancelSnooze: "Cancel",
  },

  connectors: {
    title: "Connected services",
    // Empty state — web can't approve (the 6-digit approve is phone-only),
    // so the hint points the user at the phone to finish a connection.
    emptyBody:
      "No connected services yet. Connect ChatGPT or Claude from their connector settings, then approve the code on your phone.",
    scopeSend: "Can send notifications",
    /** Fragment joined at the call site as "Added {date} · Last used {relative}";
     *  must read naturally beside `lastUsed` after the middle dot. */
    added: (date: string): string => `Added ${date}`,
    /** Fragment joined at the call site as "Added {date} · Last used {relative}";
     *  must read naturally beside `added` after the middle dot. */
    lastUsed: (relative: string): string => `Last used ${relative}`,
    neverUsed: "Never used",
    revoke: "Disconnect",
    revokeConfirm: (label: string): string =>
      `Disconnect ${label}? It will lose access immediately.`,
    revokeAction: "Disconnect",
    revokeConfirmLabel: (label: string): string => `Disconnect ${label}`,
    cancel: "Cancel",
    loadError: "Couldn’t load connected services. Please try again.",
    revokeError: "Couldn’t disconnect the service. Please try again.",
    retry: "Retry",
  },

  feedback: {
    title: "Send feedback",
    close: "Close",
    messageLabel: "Your message",
    messagePlaceholder: "Found a bug, have an idea, or something feels off? Tell us.",
    categoryLabel: "Category",
    categoryBug: "Bug",
    categoryIdea: "Idea",
    categoryOther: "Other",
    emailLabel: "Email (optional, only if you'd like a reply)",
    emailPlaceholder: "you@example.com",
    emailHelp: "We won't add you to any list.",
    disclosure:
      "Your message is sent to Reflecto's server and recorded in our internal issue tracker. We attach your app version and a device ID assigned during pairing so we can correlate reports. We don't see your notifications, contacts, or any personal data. Email is optional — provide it only if you'd like a reply. We don't add you to any list.",
    submit: "Send feedback",
    sending: "Sending…",
    success: "Thanks — read by a human, usually within 24h",
    error: "Couldn't send. Please try again.",
    /** Button that dismisses the feedback dialog after a successful submit
     *  — an action ("done, close it"), not the status adjective. */
    done: "Done",
    // Settings entry-point label (opens the modal). Intentionally identical to
    // `submit` (the in-modal button) — distinct keys so locales can diverge.
    sendFeedback: "Send feedback",
  },

  glance: {
    /** Panel heading. Wordplay on the feature's brand name "Glance" — the
     *  name is never translated (glossary D1); non-English locales use
     *  "Glance" itself as the heading. */
    title: "At a glance",
    appName: "Reflecto",
    closeLabel: "Close",
    aiSummaryTag: "AI summary",
    errorAi: "Couldn't generate a summary right now.",
    retryCta: "Try again",
    // Onboarding card shown when the on-device model is downloadable (new-user path).
    downloadHeading: "Get AI summaries",
    downloadBody:
      "Catch up on what you missed with an on-device AI recap. Runs entirely on your device.",
    downloadCta: "Download model",
    downloadPrivacyNote: "A one-time download. The server never sees your notifications.",
    downloadingHeading: "Downloading model…",
    caughtUp: "You're all caught up — nothing needs you right now.",
    /** Digest section heading — items that need the user's attention. */
    needsYou: "Needs you",
    /** Digest section heading — the non-urgent remainder. Unrelated to
     *  sound or Do Not Disturb. */
    quiet: "Quiet",
    /** Full scanning-status line, e.g. "Summarizing 6…" — takes the number of
     *  items still pending so locales control word order around the count. */
    scanning: (pending: number): string => `Summarizing ${pending}…`,
    jump: "Open notification",
    affordanceReply: "Reply",
    /** Imperative-verb CTA chip on a digest row. */
    affordanceOpen: "Open",
    affordanceCopyOtp: "Copy code",
    /** aria-label of the header button that opens the AI recap. "Glance" is
     *  the feature's brand name — never translated (glossary D1). */
    buttonLabel: "Glance",
  },

  update: {
    title: "Update available",
    description: "A new version of Reflecto is ready. Reload to update.",
    reload: "Reload",
  },

  pairConfirm: {
    /** Body copy on the post-pair confirm card. The smart-quotes match
     *  Android's `pair_confirm_card_message` so the two surfaces read
     *  identically; the rendered template inlines the auto-name as a
     *  bolded `<strong>` slot, hence the explicit `{name}` token. */
    cardMessage: (name: string): string => `We named this device “${name}”.`,
    /** Casual approval button confirming the auto-suggested device name —
     *  the intent is "accept / approve", not an aesthetic judgment. */
    cardLooksGood: "Looks good",
    cardEdit: "Edit",
    cardAriaLabel: "Confirm device name",
    renameTitle: "Rename this device",
    renameLabel: "Device name",
    renameSave: "Save",
    renameCancel: "Cancel",
    renameFailed: "Couldn’t rename. Please try again.",
  },

  collision: {
    title: (name: string): string => `You already have a “${name}”`,
    subtitle: "What should we call this one?",
    chipWork: (name: string): string => `Work ${name}`,
    chipHome: (name: string): string => `Home ${name}`,
    chipPersonal: (name: string): string => `Personal ${name}`,
    customLabel: "Custom name",
    save: "Save",
    useAnyway: "Use the original name anyway",
    saveFailed: "Couldn't save the new name. Please try again.",
    ariaLabel: "Resolve duplicate device name",
  },

  clipboard: {
    /** Dock section heading + ClipDock ARIA sectionLabel. */
    title: "Clipboard",
    /** Empty-state copy when no clips have arrived this session. */
    empty: "Clips from your other devices show up here. Tap to copy.",
    /** Provenance prefix on each card — passed the origin device label. */
    from: (label: string): string => `from ${label}`,
    /** Copy button aria-label (idle) + copied flash. */
    copy: "Copy to clipboard",
    copied: "Copied",
    /** Reveal toggle aria-labels for a sensitive (masked) clip. */
    reveal: "Reveal",
    hide: "Hide",
    /** sr-only hint announced while a masked clip's content is hidden. */
    maskedHint: "Content hidden — press Reveal to show",
    /** Chips on a card. */
    sensitiveChip: "Sensitive",
    cloudChip: "from your API token",
    /** Clipboard send strings — dock (sendPlaceholder/sendCta/sendSensitiveToggle/sendSuccess/sendNoDevices) + ShareTargetPage (sendPartial/sendFailed/sendEmpty). */
    sendPlaceholder: "Paste text to send…",
    sendCta: "Send",
    sendSensitiveToggle: "Mark sensitive",
    /** Result toasts for the manual send. `{n}`/`{total}` interpolated;
     *  the "device(s)" noun is `selectPlural`-driven. */
    sendSuccess: (n: number): string =>
      `Sent to ${n} ${selectPlural("en", n, { one: "device", other: "devices" })}.`,
    /** Renders like "Reached 2/3 devices." — the noun agrees with `total`
     *  via `selectPlural`, not `n`. */
    sendPartial: (n: number, total: number): string =>
      `Reached ${n}/${total} ${selectPlural("en", total, { one: "device", other: "devices" })}.`,
    sendNoDevices: "No paired devices to send to.",
    sendFailed: "Couldn't send. Please try again.",
    sendEmpty: "Nothing to send.",
    /** ClipDock bar/popover/expanded labels. */
    dockExpand: "Show clipboard history",
    dockCollapse: "Collapse",
    dockSendTitle: "Send to your devices",
    dockFromClipboard: (chars: number): string =>
      `From clipboard · ${chars} ${selectPlural("en", chars, {
        one: "character",
        other: "characters",
      })}`,
    dockSend: (n: number): string => `Send to ${n}`,
    dockCancel: "Cancel",
    dockRecentHeading: "Recent",
    dockEmptyClipboard: "Your clipboard is empty.",
    /** Web Share Target landing page. */
    shareTitle: "Send to your devices",
    shareReview:
      "Review what you're sending. It goes to your paired devices, end-to-end encrypted.",
    shareSend: "Send",
    shareCancel: "Cancel",
    shareNotPaired: "Pair a device first to send shared text.",
    shareOpenApp: "Open Reflecto",
  },
  install: {
    title: "Install Reflecto",
    body: "Add to your home screen for faster access.",
    cta: "Install",
    dismissAria: "Dismiss install prompt",
  },
  common: {
    loading: "Loading",
  },
  errors: {
    notPaired: "Not paired",
  },
  device: {
    // Joins the UA-derived browser + OS brand names into this device's
    // display name ("Chrome on macOS"). The brand names never localize;
    // only this template does. Persisted server-side and shown on the
    // phone's Devices tab verbatim.
    nameTemplate: (browser: string, os: string) => `${browser} on ${os}`,
    // Tier-C fallback when the UA yields no usable browser/OS parts.
    webFallback: "Web Browser",
  },
  // ⌘K command palette (docs/superpowers/specs/2026-07-12-extension-command-palette-design.md
  // §11). Most command labels reuse existing entries elsewhere in this
  // catalog (list.markAllRead, header.publicModeToggleEnable/Disable,
  // dnd.snooze1h, header.openSettings) — only the chrome strings and the
  // commands with no existing equivalent get their own key here.
  palette: {
    /** Search input placeholder. */
    placeholder: "Type a command…",
    /** Shown when no command matches the typed query. */
    noResults: "No matching commands",
    /** Dialog + listbox aria-label. */
    ariaLabel: "Command palette",
    /** GLOBAL clear — ignores the active search, unlike list.clearAll. */
    clearAll: "Clear all notifications",
    glance: "Glance recap",
    cancelSnooze: "Cancel snooze",
    copyClip: "Copy last received clip",
  },
} as const;
